import { ConventionalCategory, ConventionalGenre } from '../../../genre-taxonomy/types/GenreTaxonomy'

// Option model behind the segment filter's "+ Add" popovers and selected chips.
// `parentName` is carried so a chip can prefix its parent when more than one
// parent is in play ("Casino · Slots" vs. "Casual · Slots").
export interface SegmentOption {
  id: string
  name: string
  parentId: string | null
  parentName: string | null
}

// Options bucketed under their immediate parent. The popover shows the parent
// as a small header only when there is more than one group.
export interface SegmentOptionGroup {
  parentId: string | null
  parentName: string | null
  options: SegmentOption[]
}

// A node whose scope a level inherits without narrowing it: the Genre row shows
// "All Casual genres" while Casual is selected but none of its genres are.
export interface InheritedScope {
  id: string
  name: string
  // Which level the inherited node lives on, so the row can pick the right copy
  // ("All {category} subgenres" reads differently from "All {genre} subgenres").
  level: 'category' | 'genre'
}

export function categoryOptionGroups(taxonomy: readonly ConventionalCategory[]): SegmentOptionGroup[] {
  return [
    {
      parentId: null,
      parentName: null,
      options: taxonomy.map((category) => ({ id: category.id, name: category.name, parentId: null, parentName: null })),
    },
  ]
}

// Every genre in the taxonomy, grouped per category, with the categories in
// scope listed first. Categories are alphabetical within the in-scope and the
// out-of-scope block (see `scopeFirst`).
//
// The picker deliberately offers more than the current scope. Hiding the rest
// made widening a segment impossible from here: to add a Casino genre while
// Casual was selected you first had to work out that the category above was the
// thing in your way. Picking a genre from outside the scope pulls its category
// in (see `setGenres`), so the rows still read as a path down the tree.
export function genreOptionGroups(taxonomy: readonly ConventionalCategory[], selectedCategoryIds: readonly string[]): SegmentOptionGroup[] {
  return scopeFirst(taxonomy, scopedCategories(taxonomy, selectedCategoryIds)).map((category) => ({
    parentId: category.id,
    parentName: category.name,
    options: category.genres.map((genre) => ({ id: genre.id, name: genre.name, parentId: category.id, parentName: category.name })),
  }))
}

// Every subgenre in the taxonomy, grouped per genre, with the genres in scope
// (the selected ones, else those of the selected categories) listed first. Same
// reasoning as `genreOptionGroups`: the scope orders the list, it doesn't
// truncate it. Genres are alphabetical within each block.
export function subgenreOptionGroups(
  taxonomy: readonly ConventionalCategory[],
  selectedCategoryIds: readonly string[],
  selectedGenreIds: readonly string[]
): SegmentOptionGroup[] {
  const allGenres = taxonomy.flatMap((category) => category.genres)
  return scopeFirst(allGenres, scopedGenres(taxonomy, selectedCategoryIds, selectedGenreIds)).map((genre) => ({
    parentId: genre.id,
    parentName: genre.name,
    options: genre.subgenres.map((subgenre) => ({ id: subgenre.id, name: subgenre.name, parentId: genre.id, parentName: genre.name })),
  }))
}

// `all`, reordered so the in-scope nodes come first. Each block (in scope, then
// out of scope) is listed alphabetically by name (A–Z) rather than in taxonomy
// order, so the group headers read the same way the options under them do. With
// nothing selected every node is in scope, which makes the whole list plain A–Z.
function scopeFirst<T extends { id: string; name: string }>(all: readonly T[], inScope: readonly T[]): T[] {
  const byName = (a: T, b: T) => a.name.localeCompare(b.name)
  const scoped = new Set(inScope.map((node) => node.id))
  return [...all.filter((node) => scoped.has(node.id)).sort(byName), ...all.filter((node) => !scoped.has(node.id)).sort(byName)]
}

// Drops options with no blueprints behind them (count 0 / absent) so the popover
// doesn't offer nodes that would empty the page — but never drops an active
// selection. Options within each group are listed alphabetically (A–Z); the
// group order (already A–Z within the in-scope and out-of-scope blocks, in-scope
// parents first, see `scopeFirst`) is left as is. Groups left empty disappear.
export function pruneAndOrderGroups(
  groups: SegmentOptionGroup[],
  counts: Record<string, number> | undefined,
  selectedIds: readonly string[]
): SegmentOptionGroup[] {
  const selected = new Set(selectedIds)
  return groups
    .map((group) => ({
      ...group,
      options: group.options
        .filter((option) => !counts || (counts[option.id] ?? 0) > 0 || selected.has(option.id))
        .sort((a, b) => a.name.localeCompare(b.name)),
    }))
    .filter((group) => group.options.length > 0)
}

export function flattenGroups(groups: readonly SegmentOptionGroup[]): SegmentOption[] {
  return groups.flatMap((group) => group.options)
}

// Which selected categories the Genre row inherits from: every selected category
// that has no selected genre of its own.
export function inheritedGenreScopes(
  taxonomy: readonly ConventionalCategory[],
  selectedCategoryIds: readonly string[],
  selectedGenreIds: readonly string[]
): InheritedScope[] {
  const genreSet = new Set(selectedGenreIds)
  return scopedCategories(taxonomy, selectedCategoryIds)
    .filter((category) => selectedCategoryIds.includes(category.id) && !category.genres.some((genre) => genreSet.has(genre.id)))
    .map((category) => ({ id: category.id, name: category.name, level: 'category' as const }))
}

// Which nodes the Subgenre row inherits from, resolved per category branch the
// same way `resolveEffectiveSubgenreIds` scopes the data query, so the ghost
// chips always match what the page is filtered by:
//  • a selected category with no selected genre → the category ("All {category} subgenres");
//  • a selected genre with no selected subgenre → the genre ("All {genre} subgenres").
// The two mix when several categories are selected and only some are narrowed to
// a genre. A category repeated here is also on the Genre row; each row states its
// own level's scope.
export function inheritedSubgenreScopes(
  taxonomy: readonly ConventionalCategory[],
  selectedCategoryIds: readonly string[],
  selectedGenreIds: readonly string[],
  selectedSubgenreIds: readonly string[]
): InheritedScope[] {
  const categorySet = new Set(selectedCategoryIds)
  const genreSet = new Set(selectedGenreIds)
  const subgenreSet = new Set(selectedSubgenreIds)
  return taxonomy.flatMap((category): InheritedScope[] => {
    const selectedGenres = category.genres.filter((genre) => genreSet.has(genre.id))
    if (selectedGenres.length === 0) {
      return categorySet.has(category.id) ? [{ id: category.id, name: category.name, level: 'category' }] : []
    }
    return selectedGenres
      .filter((genre) => !genre.subgenres.some((subgenre) => subgenreSet.has(subgenre.id)))
      .map((genre) => ({ id: genre.id, name: genre.name, level: 'genre' }))
  })
}

function scopedCategories(taxonomy: readonly ConventionalCategory[], selectedCategoryIds: readonly string[]): ConventionalCategory[] {
  if (selectedCategoryIds.length === 0) return [...taxonomy]
  const selected = new Set(selectedCategoryIds)
  return taxonomy.filter((category) => selected.has(category.id))
}

function scopedGenres(
  taxonomy: readonly ConventionalCategory[],
  selectedCategoryIds: readonly string[],
  selectedGenreIds: readonly string[]
): ConventionalGenre[] {
  const categories = scopedCategories(taxonomy, selectedCategoryIds)
  const genres = categories.flatMap((category) => category.genres)
  if (selectedGenreIds.length === 0) return genres
  const selected = new Set(selectedGenreIds)
  return genres.filter((genre) => selected.has(genre.id))
}
